import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Eye, Download, X, AlertCircle, ChevronLeft, ChevronRight, Maximize2, ExternalLink } from 'lucide-react';
import { createPortal } from 'react-dom';
import api from '../utils/api';

const DocumentCard = ({ document: docData, className, isMobile }) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewUrl, setViewUrl] = useState(null);
  const [viewError, setViewError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [softPodData, setSoftPodData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [fileExt, setFileExt] = useState(null);

  const handleView = async (e) => {
    e.preventDefault();
    setViewError(null);
    setIsLoading(true);
    setSoftPodData(null);
    setCurrentPage(0);

    if (docData.is_soft_pod) {
      setSoftPodData({
        signed_by: docData.signed_by,
        notes: docData.notes,
        timestamp: docData.uploaded_at
      });
      setViewerOpen(true);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get(`/api/documents/${docData.id}/view-url`);
      if (response.data.is_soft_pod) {
        setSoftPodData(response.data.soft_pod_data);
      } else {
        const urls = Array.isArray(response.data.view_url) 
          ? response.data.view_url 
          : [response.data.view_url];
        setPages(urls);
        setViewUrl(urls[0]);
        // Store file extension from API response
        setFileExt(response.data.file_ext || response.data.file_extension);
      }
      setViewerOpen(true);
    } catch (error) {
      console.error('Error viewing document:', error);
      if (error.response?.data?.is_soft_pod) {
        setSoftPodData(error.response.data.soft_pod_data);
        setViewerOpen(true);
      } else {
        setViewError('Failed to load document viewer');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (e) => {
    e.preventDefault();

    if (docData.is_soft_pod === 1) {
        console.log('Cannot download soft POD');
        return;
    }

    try {
        const response = await api.get(`/api/documents/${docData.id}`, {
            responseType: 'blob',
            params: { download: 'true' },
            headers: {
                'Accept': 'application/octet-stream',
            }
        });
        
        // Get filename from Content-Disposition header or generate one
        let filename = '';
        const disposition = response.headers['content-disposition'];
        if (disposition && disposition.includes('filename=')) {
            filename = disposition.split('filename=')[1].replace(/"/g, '');
        } else {
            const extension = docData.file_extension || 
                            (response.headers['content-type'] === 'application/pdf' ? '.pdf' : 
                             response.headers['content-type'] === 'application/zip' ? '.zip' : '.unknown');
            filename = `${docData.type}_${docData.id}${extension}`;
        }
        
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        
        document.body.appendChild(a);
        a.click();
        
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        }, 100);
    } catch (error) {
        console.error('Error downloading document:', error);
    }
  };

  const handleNextPage = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(prev => prev + 1);
      setViewUrl(pages[currentPage + 1]);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
      setViewUrl(pages[currentPage - 1]);
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setViewUrl(null);
    setPages([]);
    setCurrentPage(0);
    setSoftPodData(null);
    setViewError(null);
  };

  return (
    <>
      <div className={`
        flex items-center justify-between p-3 
        bg-gray-50 rounded-md hover:bg-gray-100 transition-colors
        ${isMobile ? 'w-full' : ''}
        ${className || ''}
        ${docData.type === 'proof_of_delivery' ? 'border-l-4 border-blue-500' : ''}
      `}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="p-2 bg-white rounded-md flex-shrink-0">
            <FileText className="w-4 h-4 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900 truncate">
                {docData.document_type_display}
              </p>
              {docData.type === 'proof_of_delivery' && docData.stop_sequence && (
                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                  Stop {docData.stop_sequence + 1}
                </span>
              )}
            </div>
            {!isMobile && (
              <p className="text-xs text-gray-500 truncate">
                {docData.uploaded_by?.name}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-1 ml-2 flex-shrink-0">
          <button
            onClick={handleView}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={handleDownload}
            disabled={docData.is_soft_pod}
            className={`p-2 rounded-lg transition-colors ${
              docData.is_soft_pod 
                ? 'text-gray-300 cursor-not-allowed' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Document Viewer Modal - Rendered via Portal for proper z-index */}
      {viewerOpen && createPortal(
        <AnimatePresence>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          >
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={handleCloseViewer}
            />
            
            {/* Modal Container */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white rounded-2xl shadow-2xl w-[95vw] h-[90vh] max-w-7xl flex flex-col overflow-hidden"
            >
              {/* Header Bar */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{docData.document_type_display || 'Document'}</h3>
                    <p className="text-xs text-gray-500">
                      {docData.uploaded_by?.name && `Uploaded by ${docData.uploaded_by.name}`}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {viewUrl && (
                    <a
                      href={viewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                  <button
                    onClick={handleDownload}
                    disabled={docData.is_soft_pod}
                    className={`p-2 rounded-lg transition-colors ${
                      docData.is_soft_pod 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                    title="Download"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleCloseViewer}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            
              {/* Content Area */}
              <div className="flex-1 overflow-auto bg-slate-100 p-4">
                {isLoading ? (
                  <div className="flex flex-col justify-center items-center h-full gap-4">
                    <div className="relative">
                      <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-pulse"></div>
                      <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                    </div>
                    <p className="text-gray-500 font-medium">Loading document...</p>
                  </div>
                ) : softPodData ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-8">
                    <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
                      <AlertCircle className="w-10 h-10 text-amber-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Soft POD</h3>
                    <p className="text-gray-600 mb-6 max-w-md">
                      This is a soft proof of delivery. No physical document is available.
                    </p>
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 max-w-sm w-full">
                      {softPodData.signed_by && (
                        <div className="flex justify-between py-2 border-b border-gray-100">
                          <span className="text-gray-500">Signed by</span>
                          <span className="font-medium text-gray-900">{softPodData.signed_by}</span>
                        </div>
                      )}
                      {softPodData.notes && (
                        <div className="py-2 border-b border-gray-100">
                          <span className="text-gray-500 block mb-1">Notes</span>
                          <span className="text-gray-900">{softPodData.notes}</span>
                        </div>
                      )}
                      {softPodData.timestamp && (
                        <div className="flex justify-between py-2">
                          <span className="text-gray-500">Timestamp</span>
                          <span className="font-medium text-gray-900">{new Date(softPodData.timestamp).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : viewError ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                    <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                      <AlertCircle className="w-8 h-8 text-red-500" />
                    </div>
                    <p className="text-lg font-medium text-gray-900 mb-2">Failed to load document</p>
                    <p className="text-gray-500">{typeof viewError === 'string' ? viewError : 'An error occurred'}</p>
                  </div>
                ) : (
                  <div className="relative h-full flex flex-col bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="flex-1 min-h-0 relative">
                      {(() => {
                        const extension = (fileExt || docData.file_ext || docData.file_extension || '').toLowerCase().replace('.', '');
                        const urlLower = viewUrl?.toLowerCase() || '';
                        
                        const isPdf = extension === 'pdf' || 
                                     urlLower.includes('.pdf') ||
                                     urlLower.includes('content-type=application/pdf');
                        
                        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
                        const isImage = imageExtensions.includes(extension) ||
                                       imageExtensions.some(ext => urlLower.includes(`.${ext}`));
                        
                        const nonViewableExtensions = ['doc', 'docx', 'xls', 'xlsx', 'zip', 'rar'];
                        const isNonViewable = nonViewableExtensions.includes(extension);
                        
                        if (isPdf) {
                          return (
                            <object
                              data={viewUrl}
                              type="application/pdf"
                              className="w-full h-full"
                            >
                              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                                <div className="w-20 h-20 rounded-2xl bg-blue-100 flex items-center justify-center mb-6">
                                  <FileText className="w-10 h-10 text-blue-500" />
                                </div>
                                <p className="text-lg font-medium text-gray-900 mb-2">PDF Viewer Unavailable</p>
                                <p className="text-gray-500 mb-6">Your browser cannot display this PDF directly.</p>
                                <a
                                  href={viewUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium shadow-lg shadow-blue-500/20"
                                >
                                  <ExternalLink className="w-5 h-5" />
                                  Open PDF in New Tab
                                </a>
                              </div>
                            </object>
                          );
                        } else if (isNonViewable) {
                          return (
                            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                              <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mb-6">
                                <FileText className="w-10 h-10 text-slate-500" />
                              </div>
                              <p className="text-xl font-bold text-gray-900 mb-2">
                                {extension.toUpperCase()} Document
                              </p>
                              <p className="text-gray-500 mb-6">
                                This file type cannot be previewed in the browser
                              </p>
                              <button
                                onClick={handleDownload}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium shadow-lg shadow-blue-500/20"
                              >
                                <Download className="w-5 h-5" />
                                Download to View
                              </button>
                            </div>
                          );
                        } else if (isImage) {
                          return (
                            <div className="h-full flex items-center justify-center p-4 bg-slate-50">
                              <img
                                src={viewUrl}
                                alt="Document Preview"
                                className="max-h-full max-w-full object-contain rounded-lg shadow-lg"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  setViewError('Failed to load image');
                                }}
                              />
                            </div>
                          );
                        } else {
                          return (
                            <div className="h-full flex items-center justify-center p-4 bg-slate-50">
                              <img
                                src={viewUrl}
                                alt="Document Preview"
                                className="max-h-full max-w-full object-contain rounded-lg shadow-lg"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  setViewError('Unable to preview this document');
                                }}
                              />
                            </div>
                          );
                        }
                      })()}
                    </div>

                    {/* Page navigation */}
                    {pages.length > 1 && (
                      <div className="flex justify-center items-center gap-4 py-4 bg-white border-t border-gray-100">
                        <button
                          onClick={handlePrevPage}
                          disabled={currentPage === 0}
                          className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:hover:bg-gray-100 transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        
                        <span className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium">
                          Page {currentPage + 1} of {pages.length}
                        </span>
                        
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === pages.length - 1}
                          className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:hover:bg-gray-100 transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}; 

export default DocumentCard;